/**
 * Data Adapter - Converts API responses to frontend types
 * Maps backend Vehicle model to frontend SIFTVehicle interface
 */

import type { Vehicle } from './types';
import type { SIFTVehicle, BidRecord, ListingRecord, ReconAction } from '../types/sift';

/**
 * Parse JSON fields safely
 * Returns array for array JSON, or wraps non-JSON strings in an array
 */
function parseJSONField(field: any): any {
  if (!field) return [];
  if (typeof field === 'object') return field;
  if (typeof field === 'string') {
    // Only try to parse if it looks like JSON (starts with [ or {)
    const trimmed = field.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        return JSON.parse(field);
      } catch {
        // JSON parse failed, return as single-item array
        return [field];
      }
    }
    // Not JSON, return as single-item array
    return [field];
  }
  return [];
}

/**
 * Convert API Vehicle to SIFTVehicle
 */
export function apiVehicleToSIFTVehicle(apiVehicle: Vehicle): SIFTVehicle {
  return {
    // Identity
    vin: apiVehicle.vin || '',
    seller_id: String(apiVehicle.seller_id || ''),
    seller_name: apiVehicle.seller_name || '',
    mid: apiVehicle.mid || 0,
    
    // Vehicle Details
    model_year: String(apiVehicle.model_year || ''),
    make: apiVehicle.make || '',
    model: apiVehicle.model || '',
    trim: apiVehicle.trim || '',
    color: apiVehicle.color || '',
    vehicle_type: apiVehicle.vehicle_type || '',
    body_style: apiVehicle.body_style || '',
    doors: apiVehicle.doors || '',
    drivetrain: apiVehicle.drivetrain || '',
    engine: apiVehicle.engine || '',
    fuel_type: apiVehicle.fuel_type || '',
    transmission: apiVehicle.transmission || '',
    mileage: apiVehicle.mileage ?? null,
    
    // Pricing & Valuation
    base_mmr: apiVehicle.base_mmr ?? apiVehicle.mmr ?? null,
    mmr_range: apiVehicle.mmr_range ?? null,
    adjusted_mmr: apiVehicle.adjusted_mmr ?? null,
    floor_price: apiVehicle.floor_price ?? apiVehicle.list_price ?? null,
    buy_now_price: apiVehicle.buy_now_price ?? null,
    lowest_seller_counter: apiVehicle.lowest_seller_counter ?? null,
    starting_bid_price: apiVehicle.starting_bid_price ?? null,
    allow_make_offer: apiVehicle.allow_make_offer ?? null,
    wholesale_supply_level: apiVehicle.wholesale_supply_level ?? null,
    wholesale_demand_level: apiVehicle.wholesale_demand_level ?? null,
    wholesale_avg_days_to_sell: apiVehicle.wholesale_avg_days_to_sell ?? null,
    buyer_coverage: apiVehicle.buyer_coverage ?? null,
    wholesale_sales_efficiency: apiVehicle.wholesale_sales_efficiency ?? null,
    wholesale_sales_effectiveness: apiVehicle.wholesale_sales_effectiveness ?? null,
    retail_supply_level: apiVehicle.retail_supply_level ?? null,
    retail_demand_level: apiVehicle.retail_demand_level ?? null,
    retail_avg_days_to_sell: apiVehicle.retail_avg_days_to_sell ?? null,
    retail_rmi: apiVehicle.retail_rmi ?? null,
    retail_spread: apiVehicle.retail_spread ?? null,
    retail_retention: apiVehicle.retail_retention ?? null,
    retail_avg_list_price: apiVehicle.retail_avg_list_price ?? null,
    wholesale_days_supply: apiVehicle.wholesale_days_supply ?? null,
    retail_days_supply: apiVehicle.retail_days_supply ?? null,
    cohort_year_v4: apiVehicle.cohort_year_v4 ?? null,
    cohort_mileage_v4: apiVehicle.cohort_mileage_v4 ?? null,
    cohort_condition_v4: apiVehicle.cohort_condition_v4 ?? null,
    retail_value: apiVehicle.retail_value ?? null,
    retail_value_range: apiVehicle.retail_value_range ?? null,
    retail_list_price: apiVehicle.retail_list_price ?? null,
    adj_mmr_source: apiVehicle.adj_mmr_source ?? null,
    adj_mmr_as_of: apiVehicle.adj_mmr_as_of ?? null,
    retail_value_source: apiVehicle.retail_value_source ?? null,
    retail_value_as_of: apiVehicle.retail_value_as_of ?? null,
    
    // Listing Status
    list_status: apiVehicle.list_status || '',
    list_time: apiVehicle.list_time || '',
    list_days: apiVehicle.list_days || 0,
    days_since_first_offering: apiVehicle.days_since_first_offering,
    list_expiration_time: apiVehicle.list_expiration_time || null,
    days_expired: apiVehicle.days_expired ?? null,
    days_since_reprice: apiVehicle.days_since_reprice ?? null,
    is_second_chance: apiVehicle.is_second_chance ?? null,
    channel: apiVehicle.channel || '',
    facilitation: apiVehicle.facilitation || '',
    site_location: apiVehicle.site_location || '',
    
    // Inspection
    inspect_status: apiVehicle.inspect_status || '',
    inspect_time: apiVehicle.inspect_time || null,
    days_since_inspection: apiVehicle.days_since_inspection ?? null,
    cr_grade: apiVehicle.cr_grade ?? null,
    cr_link: apiVehicle.cr_link || null,
    inspection_channel: apiVehicle.inspection_channel || null,
    inspection_type: apiVehicle.inspection_type || null,
    inspection_source: apiVehicle.inspection_source || null,
    light_status: apiVehicle.light_status || null,
    light_source: apiVehicle.light_source || null,
    title_absent_flg: apiVehicle.title_absent_flg ?? null,
    
    // Location
    state: apiVehicle.state || '',
    location_state: apiVehicle.location_state || null,
    zip_code: String(apiVehicle.zip_code || ''),
    latitude: apiVehicle.latitude ?? null,
    longitude: apiVehicle.longitude ?? null,
    auction: apiVehicle.auction || '',
    region: apiVehicle.region || '',
    
    // Territory fields (new columns from Snowflake)
    seller_zip_code: apiVehicle.seller_zip_code || '',
    data_source: apiVehicle.data_source || '',
    territory_id: apiVehicle.territory_id || '',
    parent_territory_id: apiVehicle.parent_territory_id || '',
    grandparent_territory_id: apiVehicle.grandparent_territory_id || '',
    territory_owner: apiVehicle.territory_owner || '',
    manager: apiVehicle.manager || '',
    director: apiVehicle.director || '',
    territory_zip: apiVehicle.territory_zip || '',
    territory_city: apiVehicle.territory_city || '',
    territory_state: apiVehicle.territory_state || '',
    territory_market_center: apiVehicle.territory_market_center || '',
    rds_territory: apiVehicle.rds_territory || '',
    
    // User-assigned status
    user_status: (apiVehicle.user_status as any) || null,
    
    // Engagement Metrics
    average_daily_vdp_views: apiVehicle.average_daily_vdp_views || 0,
    total_client_vdp_views: apiVehicle.total_client_vdp_views || 0,
    client_workbook_saves: apiVehicle.client_workbook_saves || 0,
    simulcast_logged_in: apiVehicle.simulcast_logged_in || '',
    photo_count: apiVehicle.photo_count || 0,
    
    // Seller Info
    seller_acquisition_time: apiVehicle.seller_acquisition_time || '',
    seller_ownership_days: apiVehicle.seller_ownership_days || 0,
    seller_acquisition_method: apiVehicle.seller_acquisition_method || '',
    
    // Bidding
    active_bids: apiVehicle.active_bids || 0,
    total_bids: apiVehicle.total_bids || 0,
    active_high_bid: apiVehicle.active_high_bid ?? null,    
    // Listing History
    listing_occurences: apiVehicle.listing_occurences || 0,
    vdp_url: apiVehicle.vdp_url || null,
    
    // JSON Fields - Parse if needed
    open_recalls: parseJSONField(apiVehicle.open_recalls) as string[],
    damages: parseJSONField(apiVehicle.damages) as string[],
    recent_announcements: parseJSONField(apiVehicle.recent_announcements) as string[],
    structured_announcements: apiVehicle.structured_announcements || null,
    remarks: apiVehicle.remarks || null,
    seller_comments: apiVehicle.seller_comments || null,
    cr_items: parseJSONField(apiVehicle.cr_items) as string[],
    recon_catalog: parseJSONField(apiVehicle.recon_catalog) as ReconAction[],
    bid_history: parseJSONField(apiVehicle.bid_history) as BidRecord[],
    listing_history: parseJSONField(apiVehicle.listing_history) as ListingRecord[],
  };
}

/**
 * Convert array of API Vehicles to SIFTVehicles
 */
export function apiVehiclesToSIFTVehicles(apiVehicles: Vehicle[]): SIFTVehicle[] {
  return apiVehicles.map(apiVehicleToSIFTVehicle);
}
